import { useState, useEffect } from 'react';

const useShippingLocations = (shippingMethodId) => {
  // За Econt (метод 2)
  const [cities, setCities] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);

  // За Speedy (метод 3)
  const [sites, setSites] = useState([]);
  const [speedy2Offices, setSpeedy2Offices] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [filteredSpeedy2Offices, setFilteredSpeedy2Offices] = useState([]);
  const [selectedSpeedy2Office, setSelectedSpeedy2Office] = useState(null);

  // Дефиниране на идентификатори за методите на доставка
  const ECONT_SHIPPING_ID = 2;
  const SPEEDY_SHIPPING_ID = 3;

  // Зареждане на градове и офиси за Econt (метод 2)
  useEffect(() => {
    if (shippingMethodId === ECONT_SHIPPING_ID) {
      fetch('/cities.json')
        .then(res => res.json())
        .then(data => {
          const citiesData = Object.values(data.results || data);
          // Сортиране на градовете по азбучен ред по име на регион, след това по име на град
          const sortedCities = citiesData.sort((a, b) => {
            const regionA = (a.region || a.regionName || '').trim();
            const regionB = (b.region || b.regionName || '').trim();
            
            // Първо сортиране по регион
            const regionCompare = regionA.localeCompare(regionB, 'bg');
            if (regionCompare !== 0) {
              return regionCompare;
            }
            
            // Ако регионите са еднакви, сортиране по име на град
            const nameA = (a.name || '').trim();
            const nameB = (b.name || '').trim();
            return nameA.localeCompare(nameB, 'bg');
          });
          setCities(sortedCities);
        });
      fetch('/offices.json')
        .then(res => res.json())
        .then(data => setOffices(Object.values(data.results || data)));
    }
  }, [shippingMethodId]);

  // Зареждане на обекти и офиси за Speedy (метод 3)
  useEffect(() => {
    if (shippingMethodId === SPEEDY_SHIPPING_ID) {
      fetch('/offices1.json')
        .then(res => res.json())
        .then(data => {
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data.results)
              ? data.results
              : Object.values(data.results || {});
          
          // Извличане на данни за офиси от сериализирани PHP полета
          const parsedOffices = list
            .map(o => {
              const ser = o.address || '';
              const postCodeMatch = ser.match(/postCode";s:\d+:"([^"]+)"/);
              const fullAddrMatch = ser.match(/fullAddressString";s:\d+:"([^"]+)"/);
              const siteNameMatch = ser.match(/siteName";s:\d+:"([^"]+)"/);
              const siteAddrMatch = ser.match(/siteAddressString";s:\d+:"([^"]+)"/);
              const siteIdMatch = ser.match(/siteId";i:(\d+)/);

              if (!fullAddrMatch || !siteNameMatch || !siteAddrMatch) return null;

              // премахване на водещото "гр. "
              const region = siteAddrMatch[1].replace(/^гр\.\s*/i, '').trim();

              return {
                id: o.id,
                site_id: siteIdMatch ? siteIdMatch[1] : o.site_id,
                region,
                siteName: siteNameMatch[1].trim(),
                fullAddr: fullAddrMatch[1].trim(),
                postCode: postCodeMatch?.[1].trim() || '',
                originalData: o
              };
            })
            .filter(Boolean);
          
          // Групиране на офиси по регион за създаване на списък с обекти
          const sitesMap = {};
          parsedOffices.forEach(office => {
            if (!sitesMap[office.region]) {
              sitesMap[office.region] = {
                id: office.site_id,
                name: office.siteName,
                region: office.region,
                postCode: office.postCode
              };
            }
          });
          
          // Преобразуване на картата с обекти в масив и сортиране по азбучен ред
          const sitesArray = Object.values(sitesMap);
          const sortedSites = sitesArray.sort((a, b) => {
            return a.region.localeCompare(b.region, 'bg');
          });
          
          setSites(sortedSites);
          setSpeedy2Offices(parsedOffices);
        });
    }
  }, [shippingMethodId]);

  // Филтриране на офиси за Econt (метод 2)
  useEffect(() => {
    if (selectedCityId) {
      const filtered = offices.filter(o => String(o.city_id) === String(selectedCityId));
      // Сортиране на офисите по азбучен ред по адрес
      const sortedOffices = filtered.sort((a, b) => {
        const addressA = (a.address || a.office_address || '').trim();
        const addressB = (b.address || b.office_address || '').trim();
        return addressA.localeCompare(addressB, 'bg');
      });
      setFilteredOffices(sortedOffices);
    } else {
      setFilteredOffices([]);
    }
  }, [selectedCityId, offices]);

  // Филтриране на офиси за Speedy (метод 3)
  useEffect(() => {
    if (selectedSiteId) {
      const filtered = speedy2Offices.filter(o => {
        return o.region === sites.find(s => String(s.id) === String(selectedSiteId))?.region;
      });
      
      // Сортиране на офисите по азбучен ред по адрес
      const sortedOffices = filtered.sort((a, b) => {
        return a.fullAddr.localeCompare(b.fullAddr, 'bg');
      });
      
      setFilteredSpeedy2Offices(sortedOffices);
    } else {
      setFilteredSpeedy2Offices([]);
    }
  }, [selectedSiteId, speedy2Offices, sites]);

  // Автоматично избиране на първия офис, когато има само един наличен за Econt (метод 2)
  useEffect(() => {
    if (filteredOffices.length === 1 && !selectedOffice) {
      setSelectedOffice(filteredOffices[0]);
    }
  }, [filteredOffices, selectedOffice]);

  // Автоматично избиране на първия офис, когато има само един наличен за Speedy (метод 3)
  useEffect(() => {
    if (filteredSpeedy2Offices.length === 1 && !selectedSpeedy2Office) {
      setSelectedSpeedy2Office(filteredSpeedy2Offices[0]);
    }
  }, [filteredSpeedy2Offices, selectedSpeedy2Office]);

  return {
    // Econt (метод 2)
    cities,
    offices,
    selectedCityId,
    setSelectedCityId,
    filteredOffices,
    selectedOffice,
    setSelectedOffice,
    
    // Speedy (метод 3)
    sites,
    speedy2Offices,
    selectedSiteId,
    setSelectedSiteId,
    filteredSpeedy2Offices,
    selectedSpeedy2Office,
    setSelectedSpeedy2Office,
    
    // Константи
    ECONT_SHIPPING_ID,
    SPEEDY_SHIPPING_ID
  };
};

export default useShippingLocations; 